import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import { quoteSteps } from "features/quote/constants";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router";
import { useContentFooter, useContentHeader } from "hooks/mainContent";
import QuoteStepper from "features/quote/components/QuoteStepper";
import QuoteFooter from "features/quote/components/QuoteFooter";
import ContractDocumentsContainer from "./ContractDocumentsContainer";
import ContractStatusContainer from "./ContractStatusContainer";
import DealerDocumentsContainer from "./DealerDocumentsContainer";
import { AssignmentTurnedIn, DoneAll, WarningSharp } from "@material-ui/icons";
import { GET_LEASING_CONTRACT_AND_DEALER_DOCUMENTS_LIST } from "features/quote/queries";
import { useMutationWithErrorHandling, useQueryWithErrorHandling } from "hooks/errorHandling";
import { useStateLens, get, set, sequence } from "@totalsoft/react-state-lens";
import { emptyObject } from "constants/defaultValues";
import { FINALIZE_QUOTE } from "../mutations";
import { invalidateApolloClientCache } from "utils/functions";
import { addToast } from "components/notifications";
import { quoteStatus } from "constants/quoteStatus";
import { useApolloClient } from "@apollo/client";
import { SEND_CONTRACTUAL_DOCUMENTS_EMAIL_LINK } from "../mutations";
import SectionTitle from "features/common/components/SectionTitle";
import styles from "../styles";

const useStyles = makeStyles(styles);

function ContractStepContainer() {
  const { t } = useTranslation();
  const classes = useStyles();
  const history = useHistory();
  const params = useParams();
  const quoteId = Number(params.quoteId);
  const [, setHeader] = useContentHeader();
  const [, setFooter] = useContentFooter();
  const quoteLens = useStateLens(emptyObject);
  const client = useApolloClient();
  const [alternativeEMail, setAlternativeEMail] = useState(null);

  const { data } = useQueryWithErrorHandling(GET_LEASING_CONTRACT_AND_DEALER_DOCUMENTS_LIST, {
    variables: { quoteId },
    onCompleted: (data) => {
      if (!data) return;
      (quoteLens |> set)(data.quote);
    },
  });

  const [showFinalStatus, setShowFinalStatus] = useState(false);

  const showContracts = useMemo(() => {
    const quoteStatusId = quoteLens.quoteStatusId |> get;
    return (
      [-4, -12, -13].indexOf(quoteStatusId) === -1 && !showFinalStatus && !!(quoteLens?.leasingContracts |> get)?.length
    );
  }, [quoteLens?.quoteStatusId, quoteLens?.leasingContracts, showFinalStatus]);

  const showContractStatus = useMemo(() => {
    return !showContracts;
  }, [showContracts]);

  const showDealerDocuments = useMemo(() => {
    return showContracts && !!(quoteLens?.dealerDocuments |> get)?.length;
  }, [quoteLens?.dealerDocuments, showContracts]);

  useEffect(() => {
    const quoteStatusId = data?.quote?.quoteStatusId;
    if ([quoteStatus.completed.id, quoteStatus.declined.id, quoteStatus.funded.id].indexOf(quoteStatusId) !== -1)
      setShowFinalStatus(true);
  }, [data?.quote?.quoteStatusId]);

  const selectedContactPerson = useMemo(() => {
    return data?.quote?.contactPersons.find((x) => x.webContactInfoId === data.quote.contactInfoId);
  }, [data?.quote?.contactInfoId, data?.quote?.contactPersons]);

  useEffect(() => {
    if (selectedContactPerson === undefined || alternativeEMail !== null) return;
    if (selectedContactPerson.length < 1 || selectedContactPerson.alternativeEMail === alternativeEMail) return;
    setAlternativeEMail(selectedContactPerson.alternativeEMail);
  }, [selectedContactPerson, alternativeEMail]);

  const [finalizeQuote] = useMutationWithErrorHandling(FINALIZE_QUOTE);

  const handleBack = useCallback(() => {
    if (showFinalStatus) {
      setShowFinalStatus(false);
      return;
    }
    history.push({ pathname: `/quotes/${quoteId}/datacompletion` });
  }, [history, quoteId, showFinalStatus]);

  const handleComplete = useCallback(() => {
    if ([-5, -6, -7].indexOf(data?.quote?.quoteStatusId) > -1) finalizeQuote({ variables: { quoteId } });
  }, [data?.quote?.quoteStatusId, quoteId, finalizeQuote]);

  const [sendContractualDocumentsEmailLink] = useMutationWithErrorHandling(SEND_CONTRACTUAL_DOCUMENTS_EMAIL_LINK, {
    onCompleted: () => {
      addToast(t("ToastNotifications.Success"), "success");
      if (data?.quote.quoteStatusId === quoteStatus.pendingDocumentsFromCustomer.id) return;
      invalidateApolloClientCache(client, { __typename: "Quote", quoteId }, [
        "quoteStatusId",
        "quoteStatusCode",
        "quoteStatusName",
      ]);
    },
  });

  const handleSendEmail = useCallback(
    (email) => {
      sendContractualDocumentsEmailLink({ variables: { quoteId, email: email } });
    },
    [quoteId, sendContractualDocumentsEmailLink]
  );

  const checkQuoteRequiredDocuments = useMemo(() => {
    const leasingContracts = quoteLens?.leasingContracts |> get;

    let checkResult = leasingContracts?.every((c) => c.requiredDocuments?.length > 0);
    if (!checkResult) return true;

    leasingContracts.forEach((c) => {
      checkResult = c.requiredDocuments.some((item) => item.isMandatory && item.files.length === 0);
    });

    return checkResult;
  }, [quoteLens]);

  useEffect(() => {
    setHeader(
      <QuoteStepper
        quoteId={quoteId}
        activeStepId={quoteSteps.contract}
        nextStepId={quoteSteps.contract}
        disabledNextStep={checkQuoteRequiredDocuments}
        onHandleNextStep={handleComplete}
      />
    );
  }, [t, setHeader, quoteId, checkQuoteRequiredDocuments, handleComplete]);

  useEffect(() => {
    setFooter(
      <QuoteFooter
        btnBackLabel={showFinalStatus ? t("Contract.ContractualDocuments") : t("General.CompleteData")}
        btnNextLabel={t("General.Finalize")}
        btnBackIcon={<AssignmentTurnedIn />}
        btnNextIcon={<DoneAll />}
        handleBack={handleBack}
        handleComplete={handleComplete}
        btnBackDisabled={false}
        btnNextDisabled={checkQuoteRequiredDocuments}
        btnNextExists={!showFinalStatus}
        quoteId={quoteId}
      />
    );
  }, [t, quoteId, setFooter, checkQuoteRequiredDocuments, handleBack, handleComplete, showFinalStatus]);

  return (
    <Grid container spacing={3}>
      {!showFinalStatus && (
        <Grid item xs={12}>
          <SectionTitle title={t("Contract.ContractualDocuments")} subtitle={t("Contract.ViewContractualDocuments")} />
        </Grid>
      )}
      {showContractStatus && (
        <Grid item xs={12}>
          <ContractStatusContainer quoteStatusId={quoteLens.quoteStatusId |> get} />
        </Grid>
      )}
      {showContracts && (
        <>
          {(quoteLens.leasingContracts |> sequence).map((leasingContractLens, index) => (
            <Grid item xs={12} key={index}>
              <ContractDocumentsContainer
                transactionId={quoteLens.transactionId |> get}
                leasingContractLens={leasingContractLens}
                alternativeEMail={alternativeEMail}
                setAlternativeEMail={setAlternativeEMail}
                onSendEmail={handleSendEmail}
                quoteStatusId={data?.quote?.quoteStatusId}
                quoteId={quoteId}
              />
            </Grid>
          ))}
        </>
      )}
      {showDealerDocuments &&
        (quoteLens?.dealerDocuments |> sequence).map((dealerDocumentLens, index) => (
          <Grid item xs={12} key={index}>
            <DealerDocumentsContainer
              key={index}
              index={index}
              dealerDocumentLens={dealerDocumentLens}
              quoteId={quoteId}
            />
          </Grid>
        ))}
      {showContracts && (
        <Grid item xs={12}>
          <div className={classes.originalsWarning}>
            <WarningSharp fontSize="small" />
            <Typography variant="caption" className={classes.paddingRightLeft10}>
              {t("Contract.OriginalSignedDocuments")}
            </Typography>
          </div>
        </Grid>
      )}
    </Grid>
  );
}

export default ContractStepContainer;
